// STSMasterTimeline — the arrangement surface for STS MasterClips.
//
// Kept separate from Timeline.tsx (TTS) because Timeline's chip model is
// Block+favIdx+resolver; MasterClips are plain (takeId, inPoint, outPoint,
// offset) with no sibling reflow. Drag / trim / context-menu are owned here.
//
// Mirrors the TTS Timeline's overtime model: the track can grow past the
// asset boundary up to 2x, with a dashed end-marker + diagonal overflow
// shading painted while chips extend into overtime. Chips carry waveform
// backgrounds for editing reference (trim handles are easier to place when
// you can see the audio).
//
// Chrome (controls row, scrollWrap, track skeleton, ruler/ticks, playhead,
// end marker, overflow shade) is rendered by <TimelineFrame>. This file
// owns the per-flavour state (refs, zoom hook, playhead drag hook, drag
// state, chip handlers, context menu) and the per-flavour visuals (chip,
// trim handles, ghost, drag popup, context menu).

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { MasterClip, STSTake } from '@shared/types';
import { TIMELINE_GRID_STEP } from '@shared/constants';
import { useSessionStore } from '@/stores/session';
import { useVideoStore } from '@/stores/video';
import {
  distinctActiveVoiceIdsForMaster,
  resolveSTSOverlaps,
} from '@/lib/timeline';
import { lastMasterClipEnd, masterClipDurationSum, resolveTimelineLength } from '@/lib/timelineLength';
import { isFlexAsset } from '@/lib/assetUi';
import { voiceColorVar, voiceDisplayName } from '@/lib/voiceMapping';
import { getWaveformSvg } from '@/lib/waveformSvg';
import { useSTSMasterPreview } from '@/hooks/useSTSMasterPreview';
import { useTimelineZoom } from '@/hooks/useTimelineZoom';
import { scheduleVideoScrub } from '@/lib/activeVideo';
import { TimelineFrame, type FrameTrack } from '@/components/Timeline/TimelineFrame';
import type { MinimapChip } from '@/components/Timeline/TimelineMinimap';
import { chipLabelFits } from '@/components/Timeline/chipLabelFit';
import { usePlayheadDrag } from '@/hooks/usePlayheadDrag';
import { formatTakeLabel } from '@/lib/stsTakeLabel';
import styles from './STSMasterTimeline.module.css';

function snap01(x: number): number {
  return Math.round(x * 10) / 10;
}

function snapUpToGrid(x: number): number {
  return Math.ceil(x / TIMELINE_GRID_STEP) * TIMELINE_GRID_STEP;
}

type DragMode = 'move' | 'trim-left' | 'trim-right';

interface DragState {
  clipId: string;
  mode: DragMode;
  pointerId: number;
  startClientX: number;
  startOffset: number;
  startInPoint: number;
  startOutPoint: number;
  takeDuration: number;
  /** User's raw cursor position — boundary-clamped, NOT sibling-clamped.
   *  The dragged chip follows the cursor freely (TTS parity). On 'move'
   *  release we apply the sibling-reflowed offsets from `ghosts` to the
   *  OTHER clips and snap this chip to its own resolver entry. */
  currentOffset: number;
  /** Predicted post-resolver positions for every OTHER clip whose offset
   *  would move because of the pin. Filtered to changed-only so the dashed
   *  ghosts only render where overlap pushed something. Empty for trim
   *  modes (trim doesn't reflow siblings). */
  ghosts: Array<{ clipId: string; offset: number }>;
  currentInPoint: number;
  currentOutPoint: number;
}

// STS overlap resolver lives in @/lib/timeline as a thin adapter over the
// shared resolveOverlapsCore — same algorithm + snap-direction guards as
// the TTS resolveTimelineOverlaps / previewResolveOverlaps.

interface ContextMenuState {
  clipId: string;
  x: number;
  y: number;
}

// Find the nearest non-overlapping offset for a clip of length `clipDur`
// given the sorted sibling spans [start, end) it must avoid. Prefers
// `desired`; if it overlaps, tries the nearest gap left or right.
function clampAgainstSiblings(
  desired: number,
  clipDur: number,
  totalDuration: number,
  siblings: Array<{ start: number; end: number }>,
): number {
  const maxOff = Math.max(0, totalDuration - clipDur);
  const candidate = Math.max(0, Math.min(maxOff, desired));
  const sorted = siblings.slice().sort((a, b) => a.start - b.start);
  const overlaps = (off: number) =>
    sorted.some((s) => off < s.end && off + clipDur > s.start);
  if (!overlaps(candidate)) return snap01(candidate);

  const gaps: Array<{ start: number; end: number }> = [];
  let cursor = 0;
  for (const s of sorted) {
    if (s.start - cursor >= clipDur) gaps.push({ start: cursor, end: s.start });
    cursor = Math.max(cursor, s.end);
  }
  if (totalDuration - cursor >= clipDur) gaps.push({ start: cursor, end: totalDuration });
  if (gaps.length === 0) return snap01(Math.max(0, Math.min(maxOff, desired)));

  let best = gaps[0].start;
  let bestDist = Infinity;
  for (const g of gaps) {
    const lo = g.start;
    const hi = Math.max(lo, g.end - clipDur);
    const cand = Math.max(lo, Math.min(hi, desired));
    const d = Math.abs(cand - desired);
    if (d < bestDist) {
      best = cand;
      bestDist = d;
    }
  }
  return snap01(Math.max(0, Math.min(maxOff, best)));
}

// --- Chip ------------------------------------------------------------------
// Split out so each chip can own its own async waveform-SVG state (mirrors
// the pattern in Timeline.tsx).

interface ChipProps {
  clip: MasterClip;
  take: STSTake;
  label: string;
  leftPx: number;
  widthPx: number;
  /** Live in-point of the clip in seconds (drag-aware). Drives the
   *  background-position-x offset so the visible slice of the full waveform
   *  is the same range the audio engine plays. */
  inPoint: number;
  /** pixels-per-second the parent timeline currently renders at. Used to
   *  size the waveform background to its natural density (full take width =
   *  `take.duration * pxPerSec`) rather than stretching it to the chip. */
  pxPerSec: number;
  isDragged: boolean;
  /** True while ANY chip is being dragged AND this is not the dragged
   *  one — gates pointer events so capture stays on the dragged chip. */
  pointerEventsDisabled: boolean;
  /** Multi-Voice (#173): when set, the whole chip is recolored via an
   *  inline `--brandcolor` override (background, border, label-pill all
   *  derive from this in CSS). Omitted for single-voice assets so they
   *  render bit-for-bit identical to before. */
  voiceColor?: string;
  onPointerDown: (
    e: React.PointerEvent<Element>,
    clip: MasterClip,
    mode: DragMode,
  ) => void;
  onPointerMove: (e: React.PointerEvent<Element>) => void;
  onPointerUp: (e: React.PointerEvent<Element>) => void;
  onContextMenu: (
    e: React.MouseEvent<HTMLDivElement>,
    clip: MasterClip,
  ) => void;
  durationLabel: string;
}

function MasterClipChip({
  clip,
  take,
  label,
  leftPx,
  widthPx,
  inPoint,
  pxPerSec,
  isDragged,
  pointerEventsDisabled,
  voiceColor,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onContextMenu,
  durationLabel,
}: ChipProps) {
  const [wfUrl, setWfUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    void getWaveformSvg(take.audioBuffer).then((url) => {
      if (!cancelled) setWfUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [take.audioBuffer]);

  const cls = [
    styles.chip,
    isDragged ? styles.dragging : '',
    pointerEventsDisabled ? styles.disabled : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Crop-not-squash: render the full take's waveform at timeline density and
  // use background-position to slide the [inPoint, outPoint] slice into the
  // chip's box. Chip width = (outPoint - inPoint) * pxPerSec, so trimming
  // pulls in the offset and reveals less of the same waveform — bars stay
  // at their natural pixel size instead of compressing horizontally.
  const fullWaveWidthPx = Math.max(1, take.duration * pxPerSec);
  const offsetPx = -inPoint * pxPerSec;

  return (
    <div
      className={cls}
      style={{
        left: `${leftPx}px`,
        width: `${widthPx}px`,
        backgroundImage: wfUrl ? `url("${wfUrl}")` : undefined,
        backgroundSize: wfUrl ? `${fullWaveWidthPx}px 100%` : undefined,
        backgroundPosition: wfUrl ? `${offsetPx}px center` : undefined,
        backgroundRepeat: wfUrl ? 'no-repeat' : undefined,
        // Multi-Voice (#173): scope --brandcolor to the voice shade for
        // this chip's subtree so background, border, and label pill all
        // adopt the voice color automatically.
        ...(voiceColor ? ({ ['--brandcolor' as string]: voiceColor } as CSSProperties) : {}),
      }}
      title={`${label} — ${durationLabel}`}
      onPointerDown={(e) => onPointerDown(e, clip, 'move')}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onClick={(e) => e.stopPropagation()}
      onContextMenu={(e) => onContextMenu(e, clip)}
      data-clip-id={clip.id}
    >
      <span
        className={styles.trimHandle}
        onPointerDown={(e) => onPointerDown(e, clip, 'trim-left')}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={(e) => e.stopPropagation()}
        data-trim="left"
      />
      {/* Hide the label when its pill would be wider than the chip's
        usable width (chip width minus the two 6px trim handles). At fully-
        zoomed-out timelines the chips collapse to thin slivers and the
        labels would overflow into a visual mess otherwise. */}
      {chipLabelFits(label, widthPx - 12) && (
        <span className={styles.chipLabel}>{label}</span>
      )}
      <span
        className={`${styles.trimHandle} ${styles.trimHandleRight}`}
        onPointerDown={(e) => onPointerDown(e, clip, 'trim-right')}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={(e) => e.stopPropagation()}
        data-trim="right"
      />
    </div>
  );
}

interface Props {
  clips: MasterClip[];
  takesById: Record<string, STSTake>;
  /** Lane order of the underlying takes — used as the legacy fallback index
   *  when a take pre-dates the persistent session/takeInSession fields.
   *  Same array the parent renders into <TakeLane>s, so the fallback label
   *  matches the lane label exactly. */
  takeLaneOrder: STSTake[];
  /** Optional right-aligned slot inside the controls row — used by STSView
   *  to mount the mixer button + popover alongside play/zoom (mirrors
   *  TTS Timeline's `mixerSlot` API). */
  mixerSlot?: ReactNode;
}

export function STSMasterTimeline({ clips, takesById, takeLaneOrder, mixerSlot }: Props) {
  const currentAssetIdx = useSessionStore((s) => s.currentAssetIdx);
  const customDurations = useSessionStore((s) => s.customDurations);
  const configAssets = useSessionStore((s) => s.config?.assets);
  const customAssets = useSessionStore((s) => s.customAssets);
  const configVoices = useSessionStore((s) => s.config?.voices);
  const videoDurations = useVideoStore((s) => s.durations);
  const updateMasterClip = useSessionStore((s) => s.updateMasterClip);
  const removeMasterClip = useSessionStore((s) => s.removeMasterClip);
  const addMasterClip = useSessionStore((s) => s.addMasterClip);
  const setMasterClipOffsets = useSessionStore((s) => s.setMasterClipOffsets);

  const targetDuration = useMemo(() => {
    const merged = [...(configAssets ?? []), ...customAssets];
    return merged[currentAssetIdx]?.targetDuration ?? null;
  }, [configAssets, customAssets, currentAssetIdx]);

  // Active asset's voiceIds — feeds the per-track resolver, chip color
  // stripes and the trackNumberPane labels. Single-voice assets keep
  // their legacy single-track render unchanged (#173).
  const assetVoiceIds = useMemo<string[]>(() => {
    const merged = [...(configAssets ?? []), ...customAssets];
    return merged[currentAssetIdx]?.voiceIds ?? [];
  }, [configAssets, customAssets, currentAssetIdx]);

  // Länge wie in der TTS-Timeline aus resolveTimelineLength — eine Stelle
  // für Anzeige-Länge und Zieh-Deckel (Spec 2026-09-21 § 6.2). `flex` gilt
  // pro Asset: nur in Multi-Lingual und nur ohne Video. Im Standard-Projekt
  // ist es immer false = boundary aus video → target → custom → default,
  // Deckel 2×, also unverändert.
  // Im Flex-Fall zeigt der Frame kein Overflow-Raster (es gibt keine
  // „offizielle" Länge, die überschritten würde), dafür die Limit-Linie am
  // Admin-Richtwert — identisch zur TTS-Timeline.
  const flex = useSessionStore((s) => {
    const merged = [...(s.config?.assets ?? []), ...s.customAssets];
    return isFlexAsset(s.config, merged[s.currentAssetIdx]);
  });
  const { boundary, dragCap } = useMemo(
    () => resolveTimelineLength({
      flex, assetIdx: currentAssetIdx, videoDurations, targetDuration, customDurations,
      lastClipEnd: lastMasterClipEnd(clips), contentDuration: masterClipDurationSum(clips),
    }),
    [flex, currentAssetIdx, videoDurations, targetDuration, customDurations, clips],
  );

  // Refs + measurement + scroll mirror live HERE so useTimelineZoom and
  // chip handlers (defined below) can read them directly without going
  // through the frame's render-prop API.
  const scrollWrapRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [wrapW, setWrapW] = useState<number>(0);
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const handleDragActiveRef = useRef(false);

  // Drag state sits above the totalDur memo so the live position/trim of a
  // dragged clip feeds into the track width (smooth grow into overtime).
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  dragRef.current = drag;

  // totalDur = max clip-end across all clips (using the dragged clip's
  // live offset/trim if any), clamped to at most 2x boundary. When boundary
  // is 0, only the hard 1s floor applies.
  const { totalDur, inOvertime } = useMemo(() => {
    let maxEnd = boundary;
    // Mirror the dragged-clip + sibling reflow into the duration sweep so
    // a chip pushed deep past the boundary grows the track in real time
    // (and pushed-right siblings can extend past too).
    const ghostMap =
      drag && drag.mode === 'move'
        ? new Map(drag.ghosts.map((g) => [g.clipId, g.offset]))
        : null;
    // Min-width visual pin (legacy lines 6515–6524). Chip width ≥ 24px,
    // so at sticky-fit a thin chip's visual right can spill past its
    // logical end. Closed-form: totalDur ≥ offset/(1 − 24/wrapW). See
    // Timeline.tsx for the full derivation — same formula here.
    const visualPinFactor = wrapW > 24 ? 1 / (1 - 24 / wrapW) : 0;
    clips.forEach((c) => {
      const live = drag && drag.clipId === c.id;
      const inP = live ? drag!.currentInPoint : c.inPoint;
      const outP = live ? drag!.currentOutPoint : c.outPoint;
      const off = live
        ? drag!.currentOffset
        : (ghostMap?.get(c.id) ?? c.offset);
      const end = off + Math.max(0, outP - inP);
      if (end > maxEnd) maxEnd = end;
      if (visualPinFactor > 0) {
        const pinned = off * visualPinFactor;
        if (pinned > maxEnd) maxEnd = pinned;
      }
    });
    const maxAllowed = boundary > 0 ? dragCap : Infinity;
    const total = Math.max(Math.min(maxEnd, maxAllowed), 1);
    return { totalDur: total, inOvertime: total > boundary + 0.01 };
  }, [clips, boundary, dragCap, drag, wrapW]);

  // Sticky-fit zoom model — same as TTS Timeline; see useTimelineZoom for
  // full description. Default is sticky so the first render shows everything
  // at once. Re-fit-on-shrink fires when the asset boundary shrinks
  // (user lowers target/custom duration, swaps to shorter video).
  const {
    pxPerSec,
    fitPxPerSec,
    stickyFit,
    pxPerSecManual,
    setPxPerSecManual,
    zoomIn,
    zoomOut,
    zoomFit,
    zoomFitTo,
  } = useTimelineZoom({
    wrapW,
    totalDur,
    scrollWrapRef,
    shrinkOnBoundary: boundary,
  });

  const trackWidth = Math.max(wrapW || 0, pxPerSec * totalDur || 0);

  const preview = useSTSMasterPreview(
    clips,
    takesById,
    totalDur,
    // Beds resolve against the display boundary, NOT the voice-widened
    // totalDur — otherwise preview placement diverges from export in overtime.
    boundary,
  );
  const playheadSec = preview.playheadSec;

  // Stop the preview on asset switch (Prev/Next keeps STSView mounted for
  // STS→STS navigation) — otherwise the old asset's schedule snapshot keeps
  // playing under the new asset's timeline. Mirrors Timeline.tsx (TTS).
  useEffect(() => {
    preview.stop();
    preview.seek(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAssetIdx]);

  // Auto-zoom toggle — when ON, clicking a MasterClip zooms into that clip
  // (anchored at the click position) and pointerup snaps back to fit. Mirror
  // of the TTS Timeline auto-zoom toggle.
  const [autoZoom, setAutoZoom] = useState<boolean>(false);

  // playFromStart: stop() then play(0) so a video-click never resumes from
  // a stale playhead position. Frame wires this onto the playback singleton.
  const playFromStart = useCallback(() => {
    preview.stop();
    void preview.play(0);
  }, [preview]);

  const [ctxMenu, setCtxMenu] = useState<ContextMenuState | null>(null);

  // Whole-shaft playhead drag is owned by usePlayheadDrag (same hook as
  // TTS Timeline). Frame consumes the displayPlayheadSec + pointerdown.
  const {
    playheadDrag,
    onPointerDown: onPlayheadPointerDown,
  } = usePlayheadDrag({
    trackRef,
    pxPerSec,
    totalDur,
    playheadSec,
    isPlaying: preview.isPlaying,
    stop: preview.stop,
    seek: preview.seek,
  });

  const displayPlayheadSec = playheadDrag ?? playheadSec;

  // Fit-to-clips — bounding-box of all master clips → zoomFitTo. Local
  // because the source (clips array) is STS-specific; the zoom + scroll
  // mechanics are owned by useTimelineZoom.
  const zoomFitClips = useCallback(() => {
    if (!wrapW) return;
    let boxStart = Infinity;
    let boxEnd = 0;
    clips.forEach((c) => {
      const dur = Math.max(0, c.outPoint - c.inPoint);
      const off = c.offset || 0;
      if (off < boxStart) boxStart = off;
      if (off + dur > boxEnd) boxEnd = off + dur;
    });
    if (!Number.isFinite(boxStart) || boxEnd <= boxStart) {
      // No clips → fall back to plain fit.
      zoomFit();
      return;
    }
    zoomFitTo({ leftSec: boxStart, span: boxEnd - boxStart });
  }, [clips, wrapW, zoomFit, zoomFitTo]);

  const onChipPointerDown = useCallback(
    (e: React.PointerEvent<Element>, clip: MasterClip, mode: DragMode) => {
      if (e.button !== 0) return;
      const take = takesById[clip.takeId];
      if (!take) return;
      const target = e.currentTarget;
      try {
        target.setPointerCapture(e.pointerId);
      } catch {
        /* no-op */
      }
      e.preventDefault();
      e.stopPropagation();
      setCtxMenu(null);
      setDrag({
        clipId: clip.id,
        mode,
        pointerId: e.pointerId,
        startClientX: e.clientX,
        startOffset: clip.offset,
        startInPoint: clip.inPoint,
        startOutPoint: clip.outPoint,
        takeDuration: take.duration,
        currentOffset: clip.offset,
        ghosts: [],
        currentInPoint: clip.inPoint,
        currentOutPoint: clip.outPoint,
      });

      // Auto-zoom: only on body-clicks (mode === 'move'), not on trim
      // handles. Zoom into the clip and anchor the click-X so the pixel
      // under the cursor stays put across the zoom commit. Release path
      // (onChipPointerUp) snaps back to sticky-fit — we never remember
      // the pre-click zoom level, matching TTS Timeline semantics.
      //
      // ratio 0.4 (chip occupies ~40% of wrap) leaves 60% of wrap for
      // anchor slack so the visibility-clamp inside zoomFitTo doesn't
      // override the cursor pin in middle-of-timeline cases. The
      // previous default (0.92) made the clip exactly fill the wrap +
      // margins, collapsing the slack to a single point and forcing the
      // chip into the same fixed scroll position regardless of where on
      // it the user clicked.
      if (autoZoom && mode === 'move') {
        const clipDur = Math.max(0, clip.outPoint - clip.inPoint);
        if (clipDur > 0) {
          zoomFitTo(
            { leftSec: clip.offset, span: clipDur, ratio: 0.4 },
            { anchorClientX: e.clientX },
          );
        }
      }
    },
    [takesById, autoZoom, zoomFitTo],
  );

  const onChipPointerMove = useCallback(
    (e: React.PointerEvent<Element>) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      if (pxPerSec <= 0) return;
      const dx = e.clientX - d.startClientX;
      const ds = dx / pxPerSec;
      // Multi-Voice (#173): chip Y is locked — drag-to-master sets
      // clip.voiceId from the source take.voiceId, and the chip stays on
      // that voice's track for its lifetime. Cross-track drag is NOT
      // supported (deliberate — moving a clip between voices would
      // muddle the source take's identity). Only `currentOffset`,
      // `currentInPoint`, `currentOutPoint` are mutated below.
      // Hard cap on overtime: chip cannot be dragged past 2x boundary.
      const maxAllowed = boundary > 0 ? dragCap : Infinity;

      if (d.mode === 'move') {
        const clipDur = d.startOutPoint - d.startInPoint;
        let desired = snapUpToGrid(d.startOffset + ds);
        desired = Math.max(0, desired);
        const maxOff = Math.max(0, maxAllowed - clipDur);
        if (desired > maxOff) desired = snap01(maxOff);
        // TTS-parity reflow: dragged chip follows cursor freely; siblings
        // shift to make room. Resolver returns a Map<clipId, newOffset> for
        // every clip including the pin (the pin entry is only different
        // from `desired` in the leftOverflow case where the resolver pushes
        // the dragged clip past stacked left-side siblings).
        //
        // Multi-Voice (#173): cross-track overlap is allowed, so only the
        // pin's voice-track participates in the resolver. Same-track-only
        // filtering keeps `resolveSTSOverlaps` purely 1-D (one track at
        // a time) — mirrors `resolveTimelineOverlapsPerTrack`'s strategy.
        const pinClip = clips.find((c) => c.id === d.clipId);
        const sameTrackClips =
          pinClip ? clips.filter((c) => c.voiceId === pinClip.voiceId) : clips;
        const resolved = resolveSTSOverlaps(sameTrackClips, d.clipId, desired, boundary, maxAllowed);
        const ghosts: Array<{ clipId: string; offset: number }> = [];
        clips.forEach((c) => {
          if (c.id === d.clipId) return;
          const r = resolved.get(c.id);
          if (r === undefined) return;
          if (Math.abs(c.offset - r) >= 0.05) ghosts.push({ clipId: c.id, offset: r });
        });
        setDrag({
          ...d,
          currentOffset: snap01(desired),
          ghosts,
        });
        // Live video scrub at the cursor's desired position (#174) —
        // rAF-throttled + seeking-guarded inside scheduleVideoScrub so a
        // streamed WebDAV source survives fast pointermove.
        scheduleVideoScrub(desired);
      } else if (d.mode === 'trim-left' || d.mode === 'trim-right') {
        // Compute the new in/out/offset for this trim move.
        let newIn = d.currentInPoint;
        let newOut = d.currentOutPoint;
        let newOffset = d.currentOffset;
        if (d.mode === 'trim-left') {
          // Left trim: inPoint moves with cursor; offset shifts so the right
          // edge stays fixed on the master timeline.
          newIn = snap01(d.startInPoint + ds);
          newIn = Math.max(0, Math.min(d.startOutPoint - 0.1, newIn));
          const delta = newIn - d.startInPoint;
          newOffset = snap01(d.startOffset + delta);
          if (newOffset < 0) {
            // Pin offset ≥ 0; compensate by pulling inPoint.
            newIn = snap01(newIn - newOffset);
            newIn = Math.max(0, Math.min(d.startOutPoint - 0.1, newIn));
            newOffset = 0;
          }
        } else {
          // trim-right: outPoint grows/shrinks. Chip's left edge fixed.
          newOut = snap01(d.startOutPoint + ds);
          newOut = Math.max(d.startInPoint + 0.1, Math.min(d.takeDuration, newOut));
          const maxOut =
            d.startInPoint + Math.max(0, maxAllowed - d.startOffset);
          if (newOut > maxOut) newOut = snap01(maxOut);
        }
        // Run the per-track resolver against a simulated clip-array where the
        // pin has its new in/out/offset. Generates ghosts for siblings that
        // would need to push — same UX as body-drag (#173: trim feedback).
        const pinClip = clips.find((c) => c.id === d.clipId);
        const ghosts: typeof d.ghosts = [];
        if (pinClip) {
          const sim = clips.map((c) =>
            c.id === d.clipId
              ? { ...c, inPoint: newIn, outPoint: newOut, offset: newOffset }
              : c,
          );
          const sameTrack = sim.filter((c) => c.voiceId === pinClip.voiceId);
          const resolved = resolveSTSOverlaps(
            sameTrack,
            d.clipId,
            newOffset,
            boundary,
            maxAllowed,
          );
          resolved.forEach((r, id) => {
            if (id === d.clipId) return;
            const c = clips.find((x) => x.id === id);
            if (!c) return;
            if (Math.abs(c.offset - r) >= 0.05) ghosts.push({ clipId: id, offset: r });
          });
        }
        setDrag({
          ...d,
          currentInPoint: newIn,
          currentOutPoint: newOut,
          currentOffset: newOffset,
          ghosts,
        });
      }
    },
    [pxPerSec, boundary, dragCap, clips],
  );

  const onChipPointerUp = useCallback(
    (e: React.PointerEvent<Element>) => {
      const d = dragRef.current;
      if (!d || d.pointerId !== e.pointerId) return;
      const target = e.currentTarget;
      try {
        target.releasePointerCapture(e.pointerId);
      } catch {
        /* no-op */
      }
      if (d.mode === 'move') {
        // Fresh resolver run on commit (TTS parity: Timeline.tsx calls
        // resolveTimelineOverlaps in updateActiveBlocks). Re-using the
        // pointermove-time `ghosts` array would skip siblings whose
        // resolved positions changed by less than the 0.05s ghost-render
        // threshold — they'd never get committed and a sub-grid overlap
        // would persist. The fresh resolve commits ALL positions; the
        // bulk setter no-ops when a clip's offset is already correct.
        //
        // Multi-Voice (#173): same same-track filter as pointermove —
        // only the pin's voice group is reflowed; cross-track overlap
        // is allowed.
        const maxAllowed = boundary > 0 ? dragCap : Infinity;
        const pinClip = clips.find((c) => c.id === d.clipId);
        const sameTrackClips =
          pinClip ? clips.filter((c) => c.voiceId === pinClip.voiceId) : clips;
        const resolved = resolveSTSOverlaps(
          sameTrackClips,
          d.clipId,
          d.currentOffset,
          boundary,
          maxAllowed,
        );
        if (resolved.size > 0) {
          setMasterClipOffsets(currentAssetIdx, resolved);
        }
      } else if (d.mode === 'trim-left' || d.mode === 'trim-right') {
        // Commit the trim itself.
        const trimChanged =
          d.mode === 'trim-left'
            ? Math.abs(d.currentInPoint - d.startInPoint) > 0.0001 ||
              Math.abs(d.currentOffset - d.startOffset) > 0.0001
            : Math.abs(d.currentOutPoint - d.startOutPoint) > 0.0001;
        if (trimChanged) {
          updateMasterClip(currentAssetIdx, d.clipId, {
            inPoint: d.currentInPoint,
            outPoint: d.currentOutPoint,
            offset: d.currentOffset,
          });
        }
        // Run the resolver against the trimmed state to push siblings that
        // would otherwise overlap (#173: trim feedback parity with body-drag).
        const maxAllowed = boundary > 0 ? dragCap : Infinity;
        const pinClip = clips.find((c) => c.id === d.clipId);
        if (pinClip) {
          const sim = clips.map((c) =>
            c.id === d.clipId
              ? {
                  ...c,
                  inPoint: d.currentInPoint,
                  outPoint: d.currentOutPoint,
                  offset: d.currentOffset,
                }
              : c,
          );
          const sameTrack = sim.filter((c) => c.voiceId === pinClip.voiceId);
          const resolved = resolveSTSOverlaps(
            sameTrack,
            d.clipId,
            d.currentOffset,
            boundary,
            maxAllowed,
          );
          // Drop the pin from the bulk-commit; its offset is already in
          // updateMasterClip above, and setMasterClipOffsets is for siblings.
          resolved.delete(d.clipId);
          if (resolved.size > 0) {
            setMasterClipOffsets(currentAssetIdx, resolved);
          }
        }
      }
      setDrag(null);

      // Auto-zoom release always snaps back to sticky-fit — we intentionally
      // do NOT remember the pre-click zoom. Same semantics as TTS Timeline
      // (commit c193398). Only fires for body drags so trim-handle releases
      // don't accidentally collapse the zoom.
      if (autoZoom && d.mode === 'move') {
        zoomFit();
      }
    },
    [currentAssetIdx, updateMasterClip, setMasterClipOffsets, autoZoom, zoomFit, clips, boundary, dragCap],
  );

  const onChipContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>, clip: MasterClip) => {
      e.preventDefault();
      e.stopPropagation();
      setCtxMenu({ clipId: clip.id, x: e.clientX, y: e.clientY });
    },
    [],
  );

  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') close();
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [ctxMenu]);

  const handleDeleteClip = useCallback(
    (clipId: string) => {
      removeMasterClip(currentAssetIdx, clipId);
      setCtxMenu(null);
    },
    [currentAssetIdx, removeMasterClip],
  );

  const handleDuplicateClip = useCallback(
    (clipId: string) => {
      const src = clips.find((c) => c.id === clipId);
      if (!src) return;
      const clipDur = src.outPoint - src.inPoint;
      const maxAllowed = boundary > 0 ? dragCap : Infinity;
      const desired = snap01(src.offset + clipDur);
      const maxOff = Math.max(0, maxAllowed - clipDur);
      const baseOffset = Math.max(0, Math.min(maxOff, desired));
      // Multi-Voice (#173): duplicate inherits src.voiceId, so the
      // sibling-clamp only needs to avoid same-voice clips. Cross-voice
      // clips live on a different track and are allowed to overlap.
      const finalOffset = clampAgainstSiblings(
        baseOffset,
        clipDur,
        maxAllowed,
        clips
          .filter((c) => c.voiceId === src.voiceId)
          .map((c) => ({
            start: c.offset,
            end: c.offset + (c.outPoint - c.inPoint),
          })),
      );
      const dup: MasterClip = {
        id: `mclip-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        takeId: src.takeId,
        inPoint: src.inPoint,
        outPoint: src.outPoint,
        offset: finalOffset,
        voiceId: src.voiceId,
      };
      addMasterClip(currentAssetIdx, dup);
      setCtxMenu(null);
    },
    [addMasterClip, clips, currentAssetIdx, boundary, dragCap],
  );

  // Minimap data — uses startOffset (NOT currentOffset) for the dragged
  // clip so the minimap rect doesn't jitter while the user drags. Mirrors
  // the legacy STS minimap behaviour exactly. Per-voice partitioning in
  // multi-voice mode (#173) consumes the voiceId field below.
  const minimapChips = useMemo<MinimapChip[]>(
    () =>
      clips.map((c) => {
        const isDragged = drag?.clipId === c.id;
        const off = isDragged ? drag!.startOffset : c.offset;
        const visibleDur = Math.max(0, c.outPoint - c.inPoint);
        return { offset: off, duration: visibleDur, voiceId: c.voiceId };
      }),
    [clips, drag],
  );

  // Suppress ruler-click while a chip drag is in progress so a release
  // over the track surface doesn't trigger a seek.
  const dragGuard = useCallback(() => drag !== null, [drag]);

  // --- Multi-Voice (#173) ------------------------------------------------
  // Distinct active voiceIds across the master clips, in asset.voiceIds
  // order (stale voiceIds — admin removed a voice mid-session — land at
  // the end so they still get a track row). isMultiVoice gates the
  // stacked-track layout AND the chip color-stripe + Voice-N label.
  // Single-voice STS assets keep the legacy single-track render bit-for-
  // bit identical to before.
  const activeVoiceIds = useMemo(
    () => distinctActiveVoiceIdsForMaster(clips, assetVoiceIds),
    [clips, assetVoiceIds],
  );
  const isMultiVoice = assetVoiceIds.length >= 2 && activeVoiceIds.length >= 2;

  // Render-helper for a single chip — shared across the single-track flat
  // list and the per-voice partitioned multi-track render.
  const renderChip = (clip: MasterClip): ReactNode => {
    const take = takesById[clip.takeId];
    if (!take) return null;
    const isDragged = drag?.clipId === clip.id;
    // The dragged chip's body follows the cursor freely
    // (drag.currentOffset). Sibling reflow shows up as dashed ghosts
    // below — TTS-parity (Timeline.tsx renders the same way).
    const offset = isDragged ? drag!.currentOffset : clip.offset;
    const inPt = isDragged ? drag!.currentInPoint : clip.inPoint;
    const outPt = isDragged ? drag!.currentOutPoint : clip.outPoint;
    const dur = Math.max(0, outPt - inPt);
    const width = Math.max(24, dur * pxPerSec);
    // Persistent label — `Take {session}-{takeInSession}` falling back to
    // the take's lane position for legacy data. Reordering clips on the
    // timeline does NOT renumber, because the label is sourced from the
    // take, not the clip's index.
    const fallbackLaneIdx = takeLaneOrder.findIndex((t) => t.id === take.id);
    const label = formatTakeLabel(
      take,
      fallbackLaneIdx >= 0 ? fallbackLaneIdx : 0,
    );
    return (
      <MasterClipChip
        key={clip.id}
        clip={clip}
        take={take}
        label={label}
        leftPx={offset * pxPerSec}
        widthPx={width}
        inPoint={inPt}
        pxPerSec={pxPerSec}
        isDragged={isDragged}
        pointerEventsDisabled={drag !== null && !isDragged}
        voiceColor={
          isMultiVoice ? voiceColorVar(clip.voiceId, assetVoiceIds) : undefined
        }
        onPointerDown={onChipPointerDown}
        onPointerMove={onChipPointerMove}
        onPointerUp={onChipPointerUp}
        onContextMenu={onChipContextMenu}
        durationLabel={`${dur.toFixed(1)}s`}
      />
    );
  };

  // Render-helper for a single ghost — partitioned by the source clip's
  // voiceId so each ghost renders in the same row as its source chip.
  const renderGhost = (g: { clipId: string; offset: number }): ReactNode => {
    const clip = clips.find((c) => c.id === g.clipId);
    if (!clip) return null;
    const dur = Math.max(0, clip.outPoint - clip.inPoint);
    const width = Math.max(24, dur * pxPerSec);
    return (
      <div
        key={`ghost-${g.clipId}`}
        className={styles.ghost}
        style={{
          left: `${g.offset * pxPerSec}px`,
          width: `${width}px`,
        }}
      />
    );
  };

  // Multi-track payload — one FrameTrack per active voice. Ghosts get
  // partitioned by their source clip's voiceId so they sit in the same
  // row as the chip they preview. Mirror of Timeline.tsx#multiTracks.
  const multiTracks = useMemo<FrameTrack[] | undefined>(() => {
    if (!isMultiVoice) return undefined;
    return activeVoiceIds.map((vid) => {
      const officialIdx = assetVoiceIds.indexOf(vid);
      const numberLabel = officialIdx >= 0 ? String(officialIdx + 1) : '?';
      const numberColor = voiceColorVar(vid, assetVoiceIds);
      const numberTitle = voiceDisplayName(vid, assetVoiceIds, configVoices ?? []);
      const trackChildren = (
        <>
          {clips.filter((c) => c.voiceId === vid).map(renderChip)}
          {drag?.ghosts
            .filter((g) => {
              const c = clips.find((x) => x.id === g.clipId);
              return c?.voiceId === vid;
            })
            .map(renderGhost)}
        </>
      );
      return {
        key: vid,
        numberLabel,
        numberColor,
        numberTitle,
        trackChildren,
      };
    });
    // renderChip + renderGhost close over the same state the deps list
    // already covers — including them would defeat the memo each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMultiVoice,
    activeVoiceIds,
    assetVoiceIds,
    clips,
    drag,
    pxPerSec,
    configVoices,
    takesById,
    takeLaneOrder,
  ]);

  const overlay = ctxMenu ? (
    <div
      className={styles.contextMenu}
      style={{ left: ctxMenu.x, top: ctxMenu.y }}
      onMouseDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      role="menu"
    >
      <button
        type="button"
        className={styles.contextItem}
        onClick={() => handleDuplicateClip(ctxMenu.clipId)}
        role="menuitem"
      >
        Duplicate
      </button>
      <button
        type="button"
        className={`${styles.contextItem} ${styles.contextItemDanger}`}
        onClick={() => handleDeleteClip(ctxMenu.clipId)}
        role="menuitem"
      >
        Delete
      </button>
    </div>
  ) : null;

  return (
    <TimelineFrame
      scrollWrapRef={scrollWrapRef}
      trackRef={trackRef}
      setWrapW={setWrapW}
      setScrollLeft={setScrollLeft}
      scrollLeft={scrollLeft}
      wrapW={wrapW}
      handleDragActiveRef={handleDragActiveRef}
      totalDur={totalDur}
      boundary={boundary}
      inOvertime={inOvertime}
      hideOverflowShade={flex}
      limitSec={flex ? targetDuration : null}
      pxPerSec={pxPerSec}
      fitPxPerSec={fitPxPerSec}
      trackWidth={trackWidth}
      pxPerSecManual={pxPerSecManual}
      stickyFit={stickyFit}
      setPxPerSecManual={setPxPerSecManual}
      zoomIn={zoomIn}
      zoomOut={zoomOut}
      zoomFit={zoomFit}
      playheadSec={playheadSec}
      displayPlayheadSec={displayPlayheadSec}
      isPlaying={preview.isPlaying}
      isPlayheadDragging={playheadDrag !== null}
      onPlayheadPointerDown={onPlayheadPointerDown}
      play={preview.play}
      stop={preview.stop}
      seek={preview.seek}
      playFromStart={playFromStart}
      mixerSlot={mixerSlot}
      fitContentLabel="Fit Clips"
      fitContentTitle="Fit clips — zoom + scroll so all clips fill the view"
      fitContentAriaLabel="Fit clips"
      onFitContent={zoomFitClips}
      autoZoom={autoZoom}
      onToggleAutoZoom={() => setAutoZoom((v) => !v)}
      minimapChips={minimapChips}
      minimapActiveVoiceIds={isMultiVoice ? activeVoiceIds : undefined}
      minimapAssetVoiceIds={isMultiVoice ? assetVoiceIds : undefined}
      onRulerClickGuard={dragGuard}
      overlaySlot={overlay}
      multiTracks={multiTracks}
    >
      {/* Single-track mode: chips + ghosts as direct children of .track.
          Multi-track mode: per-track chips + ghosts live inside
          multiTracks[i].trackChildren; only the drag-popup below is
          passed here as a cross-track overlay. */}
      {!isMultiVoice && clips.map(renderChip)}
      {!isMultiVoice && drag?.mode === 'move' && drag.ghosts.map(renderGhost)}

      {drag && (() => {
        // Anchor the popup to the trim point itself so the value sits
        // above the edge it represents:
        //   move      → chip's left edge   (= chip offset)
        //   trim-left → chip's left edge   (= in-point)
        //   trim-right → chip's right edge (= offset + visible duration)
        // Popup is `translateX(-50%)` in CSS so the `left` here is the
        // anchor centre, not the popup's own left edge.
        const visibleDur = Math.max(
          0,
          drag.currentOutPoint - drag.currentInPoint,
        );
        const anchorSec =
          drag.mode === 'trim-right'
            ? drag.currentOffset + visibleDur
            : drag.currentOffset;
        return (
          <div
            className={styles.dragPopup}
            style={{ left: `${anchorSec * pxPerSec}px` }}
          >
            {drag.mode === 'move' && `${drag.currentOffset.toFixed(1)}s`}
            {drag.mode === 'trim-left' &&
              `in ${drag.currentInPoint.toFixed(1)}s`}
            {drag.mode === 'trim-right' &&
              `out ${drag.currentOutPoint.toFixed(1)}s`}
          </div>
        );
      })()}
    </TimelineFrame>
  );
}
